import React from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const PurchaseDateHeatmap = ({ data = [] }) => {
  const navigate = useNavigate();
  
  if (!data.length) return <div>No purchase date data available</div>;

  // Get month and year from date
  const getMonthYear = (dateStr) => {
    const date = dayjs(dateStr);
    return {
      year: date.year().toString(),
      month: date.month()
    };
  };

  // Group data by year and month
  const groupedData = data.reduce((acc, item) => {
    const { year, month } = getMonthYear(item.date);
    const key = `${year}-${month}`;
    acc[key] = (acc[key] || 0) + item.count;
    return acc;
  }, {});

  // Get unique years
  const years = [...new Set(data.map(item => getMonthYear(item.date).year))].sort();

  // Get color for value using logarithmic scale
  const getColor = (value) => {
    if (!value) return '#eeeeee';
    const maxValue = Math.max(...Object.values(groupedData));
    
    // Calculate logarithmic value and category
    const logBase = 2;
    const logValue = Math.log(value) / Math.log(logBase);
    const logMax = Math.log(maxValue) / Math.log(logBase);
    const normalizedLog = logValue / logMax;

    // Define color categories with shades from light to dark
    const categoryColors = {
      low: ['#a8e6d5', '#61cdbb', '#2ab5a0', '#008b76'],     // Teal shades
      medium: ['#ffd0a0', '#ffb164', '#ff9428', '#e67000'],  // Orange shades
      high: ['#ff9e9e', '#ff6b6b', '#ff3838', '#e60000']     // Red shades
    };

    // Determine category based on logarithmic value
    const getCategory = () => {
      if (normalizedLog <= 0.33) return 'low';
      if (normalizedLog <= 0.66) return 'medium';
      return 'high';
    };

    // Get shade index within category (0-3, darker as value increases)
    const getShadeIndex = (category) => {
      const categoryRanges = {
        low: [0, 0.33],
        medium: [0.33, 0.66],
        high: [0.66, 1]
      };
      
      const [min, max] = categoryRanges[category];
      const rangeSize = max - min;
      const positionInRange = (normalizedLog - min) / rangeSize;
      return Math.min(Math.floor(positionInRange * 4), 3);
    };

    const category = getCategory();
    const shadeIndex = getShadeIndex(category);
    return categoryColors[category][shadeIndex];
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div className="h-96 w-full p-4 bg-white rounded-lg shadow">
      <h2 className="text-lg font-semibold mb-4">Acquisition Calendar</h2>
      <div className="h-80 overflow-auto">
        <table className="w-full border-separate border-spacing-1">
          <thead>
            <tr>
              <th className="text-right pr-4 w-20">Year</th>
              {monthNames.map(month => (
                <th key={month} className="text-center p-2 font-medium">
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {years.map(year => (
              <tr key={year}>
                <td className="text-right pr-4 font-medium">{year}</td>
                {Array.from({ length: 12 }, (_, month) => (
                  <td
                    key={month}
                    className="p-0"
                  >
                    <div
                      className={`aspect-square w-12 rounded flex items-center justify-center text-xs font-medium relative ${groupedData[`${year}-${month}`] ? 'cursor-pointer hover:brightness-90' : ''}`}
                      style={{
                        backgroundColor: getColor(groupedData[`${year}-${month}`])
                      }}
                      title={`${monthNames[month]} ${year}: ${groupedData[`${year}-${month}`] || 0} coins`}
                      onClick={() => {
                        if (groupedData[`${year}-${month}`]) {
                          // Format dates as YYYY-MM-DD
                          const startDate = dayjs().year(year).month(month).startOf('month').format('YYYY-MM-DD');
                          const endDate = dayjs().year(year).month(month).endOf('month').format('YYYY-MM-DD');
                          const params = new URLSearchParams(window.location.search);
                          params.set('purchase_date', `[${startDate} TO ${endDate}]`);
                          navigate(`/inventory?${params.toString()}`);
                        }
                      }}
                    >
                      {groupedData[`${year}-${month}`] || ''}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PurchaseDateHeatmap;
